import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  Terminal, 
  Mic, 
  X, 
  Maximize2, 
  Minimize2, 
  Volume2, 
  VolumeX, 
  Languages,
  Send,
  Trash2,
  ChevronDown,
  ExternalLink,
  Save,
  MessageSquare,
  Sparkles,
  RefreshCw,
  ArrowRight
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import NewVoiceAssistant from './NewVoiceAssistant';

const FloatingActions = ({ content = "" }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState(null); // 'notes', 'compiler', 'ai'
  const [isMinimized, setIsMinimized] = useState(false);
  const containerRef = useRef(null);

  // Notes State
  const [notes, setNotes] = useState(() => localStorage.getItem('user_notes') || "");
  const [isSaving, setIsSaving] = useState(false);

  // Prevent floating buttons from moving outside viewport
  useEffect(() => {
    const handleScroll = () => {
        if (containerRef.current) {
            // Optional constraint logic
        }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    localStorage.setItem('user_notes', notes);
  }, [notes]);

  const saveToBackend = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.userId) {
      alert("Please log in to save notes permanently.");
      return;
    }

    setIsSaving(true);
    try {
      // Fetch existing notes first to append/update
      const res = await axios.get(`${API_BASE_URL}/notes/${user.userId}`);
      let existingNotes = res.data;
      
      const newNote = {
        id: Date.now(),
        title: `Note from ${new Date().toLocaleDateString()}`,
        content: notes,
      };

      await axios.post(`${API_BASE_URL}/notes/${user.userId}`, {
        notes: [newNote, ...existingNotes],
      });
      alert("Note saved to your library!");
    } catch (err) {
      console.error("Failed to save note", err);
      alert("Failed to save note to backend.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderTool = () => {
    if (!activeTool) return null;

    return (
      <div className={`fixed bottom-24 right-6 w-72 rounded-3xl shadow-2xl border transition-all duration-300 z-[100] flex flex-col overflow-hidden ${
        isDark ? 'bg-[#0B0F1A] border-white/10' : 'bg-white border-gray-100'
      } ${isMinimized ? 'h-16' : 'h-[420px]'}`}>
        
        {/* Header */}
        <div className="p-4 border-b border-inherit flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-[#5B21B6]/10 text-[#5B21B6]">
              {activeTool === 'notes' && <FileText size={18} />}
              {activeTool === 'compiler' && <Terminal size={18} />}
              {activeTool === 'ai' && <Mic size={18} />}
            </div>
            <span className={`font-black uppercase tracking-widest text-xs ${isDark ? 'text-white' : 'text-[#5B21B6]'}`}>
              {activeTool === 'notes' && 'Personal Notes'}
              {activeTool === 'compiler' && 'Quick Compiler'}
              {activeTool === 'ai' && 'Voice Assistant'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setIsMinimized(!isMinimized)} className="p-2 hover:bg-white/5 rounded-lg text-gray-500">
              {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
            </button>
            <button onClick={() => setActiveTool(null)} className="p-2 hover:bg-white/5 rounded-lg text-gray-500">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {activeTool === 'notes' && (
              <div className="flex-1 p-4 flex flex-col gap-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Scratchpad</span>
                  <button 
                    onClick={() => navigate('/notes')}
                    className="flex items-center gap-1 text-[10px] font-bold text-primary hover:underline"
                  >
                    <ExternalLink size={10} /> Full Screen
                  </button>
                </div>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Type your notes here... (Auto-saves to local)"
                  className={`flex-1 w-full bg-transparent outline-none resize-none text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-700'}`}
                />
                <div className="flex gap-2">
                  <button 
                    onClick={() => setNotes('')}
                    className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold text-rose-500 hover:bg-rose-500/5 rounded-xl transition-colors border border-rose-500/10"
                  >
                    <Trash2 size={14} /> Clear
                  </button>
                  <button 
                    onClick={saveToBackend}
                    disabled={isSaving || !notes.trim()}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-xl transition-all border ${
                      isSaving || !notes.trim() 
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                      : 'bg-[#5B21B6] text-white border-[#5B21B6] shadow-lg shadow-purple-500/20 hover:scale-[1.02]'
                    }`}
                  >
                    <Save size={14} /> {isSaving ? 'Saving...' : 'Save to Library'}
                  </button>
                </div>
              </div>
            )}

            {activeTool === 'compiler' && (
              <div className="flex-1 p-6 flex flex-col items-center justify-center text-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <Terminal size={32} />
                </div>
                <div>
                  <h4 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Full Compiler Required</h4>
                  <p className="text-xs text-gray-500">Please visit the dedicated compiler page for the best multi-language coding experience.</p>
                </div>
                <a 
                  href="/compiler" 
                  target="_blank"
                  className="bg-primary text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 hover:scale-105 transition-all"
                >
                  Open Compiler
                </a>
              </div>
            )}

            {activeTool === 'ai' && (
              <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <NewVoiceAssistant compact={true} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {renderTool()}
      
      {/* Floating Buttons Group */}
      <div className="fixed bottom-8 right-8 flex flex-col gap-4 z-[110]" ref={containerRef}>
        <button 
          onClick={() => { setActiveTool('notes'); setIsMinimized(false); }}
          className={`group w-16 h-16 rounded-2xl shadow-[0_10px_30px_rgba(91,33,182,0.3)] flex items-center justify-center transition-all hover:scale-110 active:scale-95 relative border-2 ${
            activeTool === 'notes' 
            ? 'bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] text-white border-[#5B21B6] ring-4 ring-purple-500/20' 
            : isDark 
              ? 'bg-[#0B0F1A]/80 backdrop-blur-md text-gray-400 border-white/10 hover:border-purple-500/50' 
              : 'bg-white/80 backdrop-blur-md text-slate-500 border-gray-100 hover:border-purple-500/50'
          }`}
        >
          <FileText size={22} className={activeTool === 'notes' ? 'scale-110' : ''} />
          <span className="absolute right-full mr-5 px-4 py-2 rounded-2xl bg-[#111827] text-white text-[11px] font-black uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0 pointer-events-none whitespace-nowrap shadow-2xl">
            Notes
          </span>
        </button>

        <button 
          onClick={() => { setActiveTool('compiler'); setIsMinimized(false); }}
          className={`group w-16 h-16 rounded-2xl shadow-[0_10px_30px_rgba(91,33,182,0.3)] flex items-center justify-center transition-all hover:scale-110 active:scale-95 relative border-2 ${
            activeTool === 'compiler' 
            ? 'bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] text-white border-[#5B21B6] ring-4 ring-purple-500/20' 
            : isDark 
              ? 'bg-[#0B0F1A]/80 backdrop-blur-md text-gray-400 border-white/10 hover:border-purple-500/50' 
              : 'bg-white/80 backdrop-blur-md text-slate-500 border-gray-100 hover:border-purple-500/50'
          }`}
        >
          <Terminal size={22} className={activeTool === 'compiler' ? 'scale-110' : ''} />
          <span className="absolute right-full mr-5 px-4 py-2 rounded-2xl bg-[#111827] text-white text-[11px] font-black uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0 pointer-events-none whitespace-nowrap shadow-2xl">
            Compiler
          </span>
        </button>

        {/* AI Voice Assistant Box */}
        <button 
          onClick={() => { setActiveTool('ai'); setIsMinimized(false); }}
          className={`group w-16 h-16 rounded-2xl shadow-[0_10px_30px_rgba(16,185,129,0.3)] flex items-center justify-center transition-all hover:scale-110 active:scale-95 relative border-2 ${
            activeTool === 'ai' 
            ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-emerald-500 ring-4 ring-emerald-500/20' 
            : isDark 
              ? 'bg-[#0B0F1A]/80 backdrop-blur-md text-gray-400 border-white/10 hover:border-emerald-500/50' 
              : 'bg-white/80 backdrop-blur-md text-slate-500 border-gray-100 hover:border-emerald-500/50'
          }`}
        >
          <Mic size={22} className={activeTool === 'ai' ? 'scale-110' : ''} />
          <span className="absolute right-full mr-5 px-4 py-2 rounded-2xl bg-[#111827] text-white text-[11px] font-black uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0 pointer-events-none whitespace-nowrap shadow-2xl">
            AI Voice Assistant
          </span>
        </button>
      </div>
    </>
  );
};

export default FloatingActions;
